import { useQuery } from "@tanstack/react-query";
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isWeekend,
  isBefore,
  startOfDay,
  format,
} from "date-fns";
import { useTimeEntries } from "./use-time-registration";
import { useAbsences } from "./use-absences";
import { useCompanyHolidays } from "./use-company-holidays";
import { osloDateString } from "@/lib/oslo-time";

export interface TimeGap {
  date: string; // YYYY-MM-DD
  registeredHours: number;
  expectedHours: number;
  absenceHours: number;
  gap: number; // expectedHours - registeredHours
  isWeekend: boolean;
  isAbsent: boolean;
  /** Helligdag på ukedag fra `company_holidays`: 0 forventede timer (VIB-3046). */
  isHoliday: boolean;
  isFuture: boolean;
}

interface TimeGapAnalysisData {
  month: string; // YYYY-MM
  gaps: TimeGap[];
  totalGapHours: number;
  totalExpectedHours: number;
  totalRegisteredHours: number;
  gapDays: number; // Count of working days with gaps
}

const EXPECTED_DAILY_HOURS = 7.5;

/**
 * Analyzes time registration gaps for a user in a given month.
 * Returns days with missing/incomplete time entries.
 */
export function useTimeGaps(
  userId: string | null,
  targetDate: Date = new Date()
): { data: TimeGapAnalysisData | null; isLoading: boolean } {
  const monthStart = startOfMonth(targetDate);
  const monthEnd = endOfMonth(targetDate);
  const monthStr = format(monthStart, "yyyy-MM");

  const startDateStr = format(monthStart, "yyyy-MM-dd");
  const endDateStr = format(monthEnd, "yyyy-MM-dd");

  // Fetch time entries and absences
  const { data: timeEntries = [], isLoading: timeLoading } = useTimeEntries(
    userId,
    startDateStr,
    endDateStr
  );
  const { data: absences = [], isLoading: absenceLoading } = useAbsences(userId);
  // Samme kilde som kapasitetsreduksjonen i KPI-fasiten (VIB-916).
  const { data: holidays, isLoading: holidayLoading } = useCompanyHolidays(
    monthStart.getFullYear()
  );

  // Build the gap analysis
  const data = useQuery({
    queryKey: ["time-gaps", userId, monthStr],
    enabled: !!userId && !timeLoading && !absenceLoading && !holidayLoading,
    queryFn: () => {
      if (!userId) return null;

      // Get all days in the month
      const allDays = eachDayOfInterval({
        start: monthStart,
        end: monthEnd,
      });

      const now = new Date();
      const gaps: TimeGap[] = [];
      let totalGapHours = 0;
      let totalExpectedHours = 0;
      let totalRegisteredHours = 0;
      let gapDays = 0;

      allDays.forEach((day) => {
        const dayStr = format(day, "yyyy-MM-dd");
        const isWeekendDay = isWeekend(day);
        const isFutureDay = isBefore(now, startOfDay(day));

        // Skip weekends and future dates
        if (isWeekendDay || isFutureDay) {
          gaps.push({
            date: dayStr,
            registeredHours: 0,
            expectedHours: 0,
            absenceHours: 0,
            gap: 0,
            isWeekend: true,
            isAbsent: false,
            isHoliday: false,
            isFuture: isFutureDay,
          });
          return;
        }

        // start_date er timestamptz. Sammenlign på Oslo-datoen, ellers treffer
        // ingen føring dagen og alle ukedager telles som tomme (VIB-3034).
        const dayEntries = timeEntries.filter((e) => osloDateString(e.start_date) === dayStr);
        const registeredHours = dayEntries.reduce(
          (sum, e) => sum + e.duration_seconds / 3600,
          0
        );

        // Helligdag på ukedag: ingen forventning, men timer ført på dagen
        // telles med (VIB-3046).
        if (holidays?.has(dayStr)) {
          totalRegisteredHours += registeredHours;
          gaps.push({
            date: dayStr,
            registeredHours: Math.round(registeredHours * 100) / 100,
            expectedHours: 0,
            absenceHours: 0,
            gap: 0,
            isWeekend: false,
            isAbsent: false,
            isHoliday: true,
            isFuture: false,
          });
          return;
        }

        // Heldagsfravær fjerner hele dagskapasiteten. Deldagsfravær (`hours`)
        // reduserer bare forventet føring for den aktuelle datoen. KPI-SOT:
        // alle ikke-avviste fravær reduserer kapasitet — legacy-rader kan
        // fortsatt stå som "pending".
        const absenceHours = Math.min(
          EXPECTED_DAILY_HOURS,
          absences.reduce((sum, absence) => {
            if (
              absence.status === "rejected" ||
              dayStr < absence.start_date ||
              dayStr > absence.end_date
            ) {
              return sum;
            }
            if (absence.hours != null) {
              return dayStr === absence.start_date ? sum + absence.hours : sum;
            }
            return sum + EXPECTED_DAILY_HOURS;
          }, 0),
        );
        const dayIsAbsent = absenceHours > 0;

        if (absenceHours >= EXPECTED_DAILY_HOURS) {
          gaps.push({
            date: dayStr,
            registeredHours: 0,
            expectedHours: 0,
            absenceHours: EXPECTED_DAILY_HOURS,
            gap: 0,
            isWeekend: false,
            isAbsent: true,
            isHoliday: false,
            isFuture: false,
          });
          return;
        }

        const expectedHours = Math.max(0, EXPECTED_DAILY_HOURS - absenceHours);
        const gap = Math.max(0, expectedHours - registeredHours);

        if (gap > 0) {
          gapDays++;
          totalGapHours += gap;
        }

        totalExpectedHours += expectedHours;
        totalRegisteredHours += registeredHours;

        gaps.push({
          date: dayStr,
          registeredHours: Math.round(registeredHours * 100) / 100,
          expectedHours: Math.round(expectedHours * 100) / 100,
          absenceHours: Math.round(absenceHours * 100) / 100,
          gap: Math.round(gap * 100) / 100,
          isWeekend: false,
          isAbsent: dayIsAbsent,
          isHoliday: false,
          isFuture: false,
        });
      });

      return {
        month: monthStr,
        gaps,
        totalGapHours: Math.round(totalGapHours * 100) / 100,
        totalExpectedHours: Math.round(totalExpectedHours * 100) / 100,
        totalRegisteredHours: Math.round(totalRegisteredHours * 100) / 100,
        gapDays,
      };
    },
  }).data;

  return {
    data,
    isLoading: timeLoading || absenceLoading || holidayLoading,
  };
}
